<template>
  <div class="conversation-box bg-white h-100">
    <div v-if="Object.keys(conversation).length > 0" class="card">
      <conversation-header :room="conversation" :user="user" :in-page="isInPage" />
      <div :ref="`browse-file-${conversation.id}`" :class="`open-file-browser-${conversation.id}`" />

      <div
        ref="chatMessageBody"
        class="card-body"
        :style="{ minHeight: cardBodyHeight }"
        @dragover="dragOverFile"
        @dragleave="dragLeaveFile"
        @drop="dropFile"
        @scroll="scrollMessages"
      >
        <conversation-message ref="message" :is-floating="false" :room="conversation" />
        <div v-if="isDraggingFile" class="draggable-overlay" />
      </div>

      <div class="card-footer py-2 px-3" :style="{ minHeight: cardFooterHeight }">
        <div class="file-preview d-flex">
          <vue-dropzone
            :ref="`dropzone-${conversation.id}`"
            :id="'file-upload-' + conversation.id"
            :options="dropzoneOptions"
            @vdropzone-file-added="showAddedFile"
          />

          <div v-if="previewUrl !== ''" class="send-file-action">
            <button class="btn btn-xs btn-success" :disabled="isSendingFile" @click="sendFile(conversation.id)">
              <i class="fas fa-check"></i> {{ isSendingFile ? 'Sending..' : 'Send' }}
            </button>
            <button class="btn btn-xs btn-danger" :disabled="isSendingFile" @click="removeFiles(conversation.id)">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <conversation-input :avatar="account_image" :room="conversation" :user="user" @file-browser="openFileBrowser" />
      </div>
    </div>

    <div v-else class="d-flex align-items-center justify-content-center h-100">
      <h5 class="text-secondary">There is no chat selected</h5>
    </div>
  </div>
</template>

<script>
import vueDropzone from "vue2-dropzone";
import { broadcastMessage, getUserInRoom, sendMessage } from "../../../../services/api/uni-chat";
import ConversationHeader from "../../components/ConversationHeader.vue";
import ConversationInput from "../../components/ConversationInput.vue";
import ConversationMessage from "../../components/ConversationMessage.vue";

import "vue2-dropzone/dist/vue2Dropzone.min.css";

import {mapGetters} from "vuex";

export default {
  name: 'ConversationBox',
  components: {
    vueDropzone,
    ConversationMessage,
    ConversationHeader,
    ConversationInput
  },
  props: {
    conversation: {
      type: [Array, Object],
      default: []
    }
  },
  data() {
    return {
      user: [],
      userID: 0,
      status: '',
      statusLabel: '',
      account_image: '',
      isInPage: true,
      isSendingFile: false,
      isDraggingFile: false,
      isLoadingMoreMessages: false,
      chatInputHeight: 45,
      chatHeightExt: 60,
      prevScrollTop: 0,
      previewUrl: '',
      dropzoneFileAdded: false,
      dropzoneOptions: {
        url: `/admin/api/uni-chat/message/${this.conversation.id}/file`,
        maxFiles: 1,
        acceptedFiles: 'image/*,application/pdf',
        thumbnailWidth: 45,
        thumbnailHeight: 45,
        autoQueue: false,
        autoProcessQueue: false,
        uploadMultiple: false
      }
    }
  },
  computed: {
    ...mapGetters('presence', ['getUserPresence']),
    cardBodyHeight() {
      return this.dropzoneFileAdded
        ? `calc(100vh - (280px + ${this.chatHeightExt}px))`
        : `calc(100vh - 280px)`;
    },
    cardFooterHeight() {
      return this.dropzoneFileAdded
        ? `calc(${this.chatInputHeight}px + ${this.chatHeightExt}px)`
        : `${this.chatInputHeight}px`;
    }
  },
  watch: {
    conversation(value) {
      if (Object.keys(value).length > 0) {
        this.dropzoneOptions.clickable = `.open-file-browser-${value.id}`;

        if (this.conversation.type === 'single') {
          this.getUser(value.id);
        }
      }
    }
  },
  methods: {
    async getUser(room_id) {
      try {
        const response = await getUserInRoom(room_id);

        const presence = this.getUserPresence(response.data.id)

        if (presence) {
          this.user = presence
          this.userID = presence.id
          this.statusLabel = presence.status_label
        } else {
          this.user = response.data;
          this.userID = response.data.id;
          this.statusLabel = response.data.status_label;
        }

        this.account_image = response.data.account_image;
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    },
    openFileBrowser(room_id) {
      const dropzone = this.$refs[`dropzone-${room_id}`];

      if (dropzone && dropzone.getAcceptedFiles().length >= this.dropzoneOptions.maxFiles) {
        console.error("Maximum number of files exceeded!");
        return;
      }

      this.$refs[`browse-file-${room_id}`].click();
    },
    removeFiles(room_id) {
      this.previewUrl = '';

      this.$refs[`dropzone-${room_id}`].removeAllFiles();
      this.dropzoneFileAdded = false; // Reset the dropzoneFileAdded value
    },
    showAddedFile(event) {
      if (event && event.upload && event.upload.filename) {
        if (!this.dropzoneFileAdded) {
          // If a file has not been added previously, update the preview URL
          this.previewUrl = event.dataURL;
          this.dropzoneFileAdded = true; // Set to true to indicate a file has been added
        } else {
          // If a file has already been added, just update the preview URL
          this.previewUrl = event.dataURL;
        }
      } else {
        console.error('Event is undefined');
      }
    },
    dragOverFile(event) {
      event.preventDefault();
      this.isDraggingFile = true;
    },
    dragLeaveFile(event) {
      event.preventDefault();
      const relatedTarget = event.relatedTarget;

      if (!this.$el.contains(relatedTarget)) {
        this.isDraggingFile = false;
      }
    },
    dropFile(event) {
      event.preventDefault();
      this.isDraggingFile = false;

      const dropzone = this.$refs[`dropzone-${this.conversation.id}`];

      if (dropzone) { // Check if dropzone instance is available
        const files = event.dataTransfer.files;

        // Check if only one file is dragged
        if (files.length > 1) {
          console.warn("Only one file can be dropped. Ignoring additional files.");
        }

        // Get the first file
        const file = files[0];

        // Remove previously added file
        dropzone.removeAllFiles();
        dropzone.addFile(file);
      } else {
        console.error("Dropzone instance not available.");
      }
    },
    async sendFile(room_id) {
      try {
        this.isSendingFile = true;

        // Access the files added to the dropzone
        const files = this.$refs[`dropzone-${room_id}`].getAcceptedFiles();

        if (files.length === 0) {
          console.error('No files added to dropzone.');
          return;
        }

        // Create FormData object and append each file
        const formData = new FormData();
        files.forEach(file => {
          formData.append('file', file, file.name);
        });

        const response = await sendMessage(room_id, formData);

        if (response.status === 200) {
          this.previewUrl = '';
          this.$refs[`dropzone-${room_id}`].removeAllFiles();

          const channel = new BroadcastChannel('chat');
          channel.postMessage({
            type: 'sending-message',
            room_id: room_id,
            message: response.data.message
          });

          // Broadcast newly sent message based on new Message ID
          await broadcastMessage(room_id, { message_id: response.data.message.id });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        this.isSendingFile = false;
        this.dropzoneFileAdded = false;
      }
    },
    scrollMessages(event) {
      const chatMsg = event.target;
      const scrollHeight = chatMsg.scrollHeight; // Store the previous scroll height
      const allMessagesLoaded = this.$refs.message.isAllMessagesLoaded;

      // Calculate the threshold for loading more messages
      const threshold = 100; // Adjust this value as needed

      if (
        !allMessagesLoaded &&
        chatMsg.scrollTop < this.prevScrollTop && // Check if scrolling up
        chatMsg.scrollTop < threshold && // Check if scroll position is near the top
        !this.isLoadingMoreMessages
      ) {
        // Set loading flag to prevent multiple loading at once
        this.isLoadingMoreMessages = true;

        this.$refs.message.loadMoreMessages()
          .then(() => {
            const newScrollHeight = chatMsg.scrollHeight;
            const newContentHeight = newScrollHeight - scrollHeight;
            // Adjust the scroll position to maintain the previous position
            chatMsg.scrollTop = this.prevScrollTop + newContentHeight;
          })
          .catch((error) => {
            console.error('Error loading more messages:', error);
          })
          .finally(() => {
            this.isLoadingMoreMessages = false;
          });
      }

      // Update the previous scroll position
      this.prevScrollTop = chatMsg.scrollTop;
    }
  }
}
</script>

<style lang="scss" scoped>
.conversation-box {
  position: relative;
  border: none;
  border-radius: 0 3px 0 0;

  // Hover effect for top border
  &:hover::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: transparent;
    cursor: ns-resize;
  }

  // Hover effect for left border
  &:hover::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: transparent;
    cursor: ew-resize;
  }

  .card {
    border: none;
    border-radius: 0 3px 0 0;
    margin-bottom: 0;
    max-height: calc(100vh - 180px);

    .card-body {
      overflow-y: auto;
    }
  }

  .draggable-overlay {
    position: absolute;
    top: 40px;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .file-preview {
    justify-content: space-between;
    align-items: center;
  }
}

::v-deep {
  .dropzone {
    border: none;
    min-height: 0;
    padding: 0;

    .dz-preview {
      min-height: 0;
      margin: 0;

      .dz-image img {
        border-radius: 4px;
      }

      .dz-details, .dz-error-message {
        display: none;
      }

      .dz-progress {
        display: none;
        width: 30px;
        height: 10px;
        margin-top: -5px;
        margin-left: -15px;
        opacity: .5;
      }

      .dz-succcess-mark svg,  .dz-error-mark svg {
        width: 15px;
      }
    }
  }
}
</style>